import { readJsonConfig } from "./helpers/config_helpers";
import { loadProductSection, shopifyFiltersToAlgoliaFilters } from "./helpers/shopify_helper";
import { AlgoliaClient } from "./algolia";
import { onQueryChange } from "./helpers/querystring_watcher";

declare global {
  interface Window {
    __algoliaLoaderInit?: boolean;
    lastAlgoliaResponse?: any;
  }
}

interface SearchConfig {
	apiKey: string;
	appId: string;
	index: string;
	collectionId: string;
	containerId: string;
	sectionId: string;
	moreText: string;
	size: number;
}

type ProductHit = { handle: string; objectID: string };

export function chunk<T>(arr: T[], size: number): T[][] {
	if (size <= 0) throw new Error("chunk size must be > 0");
	const out: T[][] = [];
	for (let i = 0; i < arr.length; i += size) {
		out.push(arr.slice(i, i + size));
	}
	return out;
}

console.log("Algolia loader script");

function init() {
	const config = readJsonConfig<SearchConfig>("algolia");
	let first = true;
	let loading = false;

	const fallbackContainer = document.getElementById(config?.containerId || "collection-grid");

	if (!config?.index || !config?.collectionId) {
		console.warn("Algolia config missing index or collection id", config);
		if (fallbackContainer) {
			fallbackContainer.innerHTML = `<div class="col-12 text-center py-5">Impossibile caricare i prodotti.</div>`;
		}
		return;
	}

	const algoliaClient = new AlgoliaClient(config?.apiKey || "", config?.appId || "", config?.index || "");

	async function performSearch(page: number, filters?:string[]) {
		if (loading) return;
		loading = true;

		if (!config?.containerId) {
			console.warn("Container ID not found");
			loading = false;
			return;
		}
		if (!config?.sectionId) {
			console.warn("Section ID not found");
			loading = false;
			return;
		}

		const container = document.getElementById(config.containerId);
		if (!container) {
			console.warn("Container not found");
			loading = false;
			return;
		}

		try {
			if(window.lastAlgoliaResponse && window.lastAlgoliaResponse.hits?.length === 0) {
				container.innerHTML = "";
			}

			console.log("Algolia search", { page, filters });
			const response = await algoliaClient.search<ProductHit>(config?.collectionId || "", config?.size || 24, page, filters);
			if (!response || !Array.isArray(response.hits)) {
				throw new Error("Algolia response missing hits");
			}
			window.lastAlgoliaResponse = response;
			window.dispatchEvent(new CustomEvent("algolia-search-complete", { detail: response }));

			if (response.hits.length === 0) {
				const template = document.querySelector("#algolia-no-results-template") as HTMLTemplateElement | null;
				container.innerHTML = "";
				if (template?.content) {
					container.appendChild(template.content.cloneNode(true));
				} else {
					container.innerHTML = `<div class="col-12 text-center py-5">Nessun prodotto trovato</div>`;
				}
				first = false;
				return;
			}

			const chunks = chunk(response.hits, 4);

			for (const c of chunks) {
				const productSections = await Promise.all(
					c.map((hit) =>
						loadProductSection(config.sectionId, hit.handle as string)
					)
				);

				const frag = document.createDocumentFragment();

				for (const section of productSections) {
					const doc = new DOMParser().parseFromString(section, "text/html");
					const el = doc.querySelector('.shopify-section');
					if(!el) continue;

					el.removeAttribute("id");
					frag.appendChild(el);
				}
				if(first)
					container.innerHTML = "";

				container.appendChild(frag);
				first = false;
			}
		} catch (err) {
			console.error("Algolia search failed", err);
			container.innerHTML = `<div class="col-12 text-center py-5">Errore nel caricamento dei prodotti.</div>`;
		} finally {
			loading = false;
		}
	}

	let filters = shopifyFiltersToAlgoliaFilters(new URLSearchParams(location.search));

	window.addEventListener('algolia-load-more', (e) => {
		console.log('algolia-load-more', e);
		if (!window.lastAlgoliaResponse) {
			console.warn("Load more requested before first Algolia response");
			return;
		}
		performSearch((window.lastAlgoliaResponse.page || 0) + 1, filters);
	});

	console.log("Initial Algolia search", { filters });
	performSearch(0, filters).then(() => {
		const off = onQueryChange(({ oldSearch, newSearch, url }) => {
			console.log("Algolia search query changed", { oldSearch, newSearch, url });
			filters = shopifyFiltersToAlgoliaFilters(new URLSearchParams(newSearch));
			performSearch(0, filters);
		});
	});

}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => init(), { once: true });
} else {
  init();
}
